import { useEffect, useState } from "react";
import axios from "axios";
import { Copy, Check, ExternalLink, Gavel, Loader2, FileText } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { getApiUrl } from "@/utils/api";

const API = getApiUrl();
const FALLBACK_PORTAL_URL = "https://acp.planninginspectorate.gov.uk/casesearch.aspx";

const DECISION_STYLE = {
  Allowed: "bg-emerald-100 text-emerald-800 border-emerald-200",
  Dismissed: "bg-rose-100 text-rose-800 border-rose-200",
  "Split Decision": "bg-amber-100 text-amber-800 border-amber-200",
  Withdrawn: "bg-muted text-muted-foreground border-border",
};

function formatDMY(isoDate) {
  if (!isoDate) return "—";
  const parts = String(isoDate).slice(0, 10).split("-");
  if (parts.length !== 3) return isoDate;
  const [y, m, d] = parts;
  return `${d}/${m}/${y}`;
}

function Row({ label, value }) {
  if (!value) return null;
  return (
    <div className="flex items-start justify-between gap-3 py-1.5 border-b border-border/60 last:border-0">
      <span className="text-xs text-muted-foreground shrink-0">{label}</span>
      <span className="text-sm text-right">{value}</span>
    </div>
  );
}

/**
 * Shared "view appeal" modal — used by the Admin Reports sub-tab, the
 * Agent dashboard tool, and the Integrations test-lookup preview. The
 * Planning Inspectorate has no per-case deep-link (ASP.NET WebForms
 * portal, POST-only search), so we always surface the official Case
 * Search portal + the 7-digit case reference to paste in, plus a
 * copy-link action so the result can be shared with a colleague.
 */
export default function PlanningAppealDetailModal({ appeal, open, onOpenChange, publicMode = false }) {
  const [copied, setCopied] = useState(false);
  const [resolving, setResolving] = useState(false);
  const [link, setLink] = useState(null);

  useEffect(() => {
    if (!open || !appeal?.case_number) {
      setLink(null);
      return;
    }
    setResolving(true);
    axios
      .get(`${API}/${publicMode ? "public" : "property-admin"}/planning-appeals/case-link/${appeal.case_number}`, { withCredentials: !publicMode })
      .then(({ data }) => setLink(data))
      .catch(() => setLink({ url: FALLBACK_PORTAL_URL, deep_link: false }))
      .finally(() => setResolving(false));
  }, [open, appeal?.case_number, publicMode]);

  if (!appeal) return null;

  const url = link?.url || FALLBACK_PORTAL_URL;
  const isDeepLink = !!link?.deep_link;
  const documents = link?.documents || [];

  const copyLink = async () => {
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(isDeepLink ? url : `${url} (search reference: ${appeal.case_number})`);
      } else {
        throw new Error("Clipboard API unavailable");
      }
      setCopied(true);
      toast.success(isDeepLink ? "Link copied to clipboard" : `Link copied — search reference ${appeal.case_number} on the portal`);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Couldn't copy link — please copy it manually");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="planning-appeal-detail-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Gavel className="h-4 w-4" /> Appeal {appeal.case_number}
          </DialogTitle>
          <DialogDescription>{appeal.site_address}</DialogDescription>
        </DialogHeader>

        <div className="space-y-0.5">
          <Row label="Local planning authority" value={appeal.lpa_name} />
          <Row label="Case type" value={appeal.case_type} />
          <Row
            label="Decision"
            value={
              <span className={`text-xs px-2 py-1 rounded border ${DECISION_STYLE[appeal.decision] || "bg-muted text-foreground border-border"}`}>
                {appeal.decision || "Unknown"}
              </span>
            }
          />
          <Row label="Decision date" value={formatDMY(appeal.decision_date)} />
          <Row label="Procedure" value={appeal.procedure} />
          <Row label="Development type" value={appeal.development_type} />
        </div>

        <a
          href={url}
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center justify-center gap-2 w-full rounded-md bg-primary text-primary-foreground text-sm font-medium py-2 hover:opacity-90 transition-opacity"
          data-testid="planning-appeal-open-portal-link"
        >
          {resolving ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <>
              {isDeepLink ? "View Decision on GOV.UK" : "View on Planning Inspectorate Portal"}
              <ExternalLink className="h-3.5 w-3.5" />
            </>
          )}
        </a>

        {isDeepLink && documents.length > 0 && (
          <div className="flex flex-col gap-1.5" data-testid="planning-appeal-documents">
            {documents.map((doc, i) => (
              <a
                key={i}
                href={doc.url}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-2 text-sm text-primary border border-input rounded-md px-3 py-2 hover:bg-muted/40 transition-colors"
                data-testid={`planning-appeal-document-${i}`}
              >
                <FileText className="h-3.5 w-3.5 shrink-0" />
                {doc.label || "Decision document"}
                <ExternalLink className="h-3 w-3 ml-auto shrink-0 text-muted-foreground" />
              </a>
            ))}
          </div>
        )}

        <div className="flex items-center gap-2 border border-input rounded px-2 py-1.5">
          <input
            readOnly
            value={isDeepLink ? url : `${url} (ref: ${appeal.case_number})`}
            onClick={(e) => e.target.select()}
            className="flex-1 text-xs bg-transparent outline-none text-muted-foreground min-w-0"
            data-testid="planning-appeal-portal-url-input"
          />
          <button
            type="button"
            onClick={copyLink}
            className="text-xs font-medium text-primary hover:underline shrink-0 inline-flex items-center gap-1"
            data-testid="planning-appeal-copy-link-button"
          >
            {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
            {copied ? "Copied" : "Copy"}
          </button>
        </div>
        {!isDeepLink && !resolving && (
          <p className="text-xs text-muted-foreground -mt-2">
            This older appeal isn't on the GOV.UK decision service yet — search using reference{" "}
            <strong>{appeal.case_number}</strong> (last 7 digits of the full appeal reference) on the official portal.
          </p>
        )}
      </DialogContent>
    </Dialog>
  );
}
